// components/ui/animated-button.tsx
'use client'

import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { motion } from 'motion/react'
import Lottie, { LottieRefCurrentProps } from 'lottie-react'
import { cn } from '@/lib/utils'
import { ButtonProps, buttonVariants } from './button'
import {
    getLottieAnimationData,
    getNormalizationConfig,
    type LottieAnimationName
} from '@/lib/lottie/lottie-manager'
import { getNormalizedDimensions, getContainerStyles } from '@/lib/lottie/icon-config'

export interface AnimatedButtonProps
    extends Omit<ButtonProps, 'children'> {
    name?: LottieAnimationName
    lottieIcon?: LottieAnimationName
    children?: React.ReactNode
    motionActive?: boolean
    animationTrigger?: 'hover' | 'click' | 'motion-complete'
    showGuides?: boolean
    variant?: ButtonProps['variant']
    size?: ButtonProps['size']
    asChild?: boolean
}

const ICON_SIZE = 16
const CONTAINER_SIZE = 32

export type AnimatedButtonHandle = {
    triggerPlay: () => void
}

export const AnimatedButton = React.forwardRef<AnimatedButtonHandle, AnimatedButtonProps>(function AnimatedButton({
    name,
    lottieIcon,
    children,
    motionActive = false,
    animationTrigger = 'hover',
    showGuides = false,
    className,
    variant = 'default',
    size = 'default',
    asChild = false,
    onMouseEnter,
    onMouseLeave,
    onClick,
    ...props
}: AnimatedButtonProps, ref) {
    const resolvedName = lottieIcon ?? name
    const [isClient, setIsClient] = React.useState(false)
    const lottieRef = React.useRef<LottieRefCurrentProps>(null)
    const playTimeoutRef = React.useRef<number | null>(null)

    React.useEffect(() => {
        setIsClient(true)
        return () => {
            if (playTimeoutRef.current) window.clearTimeout(playTimeoutRef.current)
        }
    }, [])

    // Ensure spring is active when using motion-complete
    if (animationTrigger === 'motion-complete' && !motionActive) {
        motionActive = true
    }

    const animationData = resolvedName ? getLottieAnimationData(resolvedName) : null
    const baseScale = resolvedName ? getNormalizationConfig(resolvedName).baseScale : 1
    const dimensions = resolvedName
        ? getNormalizedDimensions(resolvedName, ICON_SIZE, baseScale)
        : { width: ICON_SIZE, height: ICON_SIZE, scale: 1, offsetX: 0, offsetY: 0 }
    const containerStyles = getContainerStyles(CONTAINER_SIZE, ICON_SIZE * baseScale)

    const motionProps = motionActive
        ? {
            whileHover: { scale: 1.05 },
            whileTap: { scale: 0.95 },
            transition: { type: 'spring' as const, stiffness: 300, damping: 20 }
        }
        : {}

    const triggerPlay = () => {
        lottieRef.current?.stop()
        lottieRef.current?.goToAndStop(0, true)
        lottieRef.current?.play()
    }

    React.useImperativeHandle(ref, () => ({ triggerPlay }))

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
        onMouseEnter?.(e)
        if (animationTrigger === 'hover') triggerPlay()
    }

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
        onMouseLeave?.(e)
    }

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e)
        if (animationTrigger === 'click') triggerPlay()
    }

    const handleMouseUp = () => {
        if (animationTrigger === 'motion-complete') {
            if (playTimeoutRef.current) window.clearTimeout(playTimeoutRef.current)
            playTimeoutRef.current = window.setTimeout(() => {
                triggerPlay()
            }, 200)
        }
    }

    const renderIcon = () => {
        if (!isClient) {
            return (
                <div
                    className="flex-shrink-0"
                    style={{ width: CONTAINER_SIZE, height: CONTAINER_SIZE }}
                />
            )
        }

        if (!animationData) return null

        return (
            <div className="relative flex-shrink-0" style={containerStyles}>
                {showGuides && (
                    <>
                        <div className="pointer-events-none absolute inset-x-0 top-[1px] h-px bg-[rgba(127,127,127,0.35)]" />
                        <div className="pointer-events-none absolute inset-x-0 bottom-[1px] h-px bg-[rgba(127,127,127,0.35)]" />
                    </>
                )}
                <div
                    style={{
                        width: `${dimensions.width}px`,
                        height: `${dimensions.height}px`,
                        transform: `scale(${dimensions.scale}) translate(${dimensions.offsetX}px, ${dimensions.offsetY}px)`,
                        filter: 'brightness(0) invert(1)'
                    }}
                >
                    <Lottie
                        lottieRef={lottieRef}
                        animationData={animationData}
                        loop={false}
                        autoplay={false}
                        style={{
                            width: `${dimensions.width}px`,
                            height: `${dimensions.height}px`,
                            filter: 'brightness(0) invert(1)',
                            overflow: 'visible'
                        }}
                    />
                </div>
            </div>
        )
    }

    const content = (
        <motion.div {...motionProps}>
            <div className="flex items-center justify-center gap-2">
                {renderIcon()}
                {children}
            </div>
        </motion.div>
    )

    // Separate motion-conflicting props from button props
    const {
        onDrag,
        onDragStart,
        onDragEnd,
        onAnimationStart,
        onAnimationEnd,
        onAnimationIteration,
        ...buttonProps
    } = props

    if (asChild) {
        return (
            <Slot
                className={cn(buttonVariants({ variant, size, className }))}
                onClick={handleClick}
                onMouseUp={handleMouseUp}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                {...buttonProps}
            >
                {content}
            </Slot>
        )
    }

    return (
        <motion.button
            className={cn('m-0', buttonVariants({ variant, size, className }))}
            onClick={handleClick}
            onMouseUp={handleMouseUp}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...motionProps}
            {...buttonProps}
        >
            {content}
        </motion.button>
    )
})